// Барабаны игрового поля: вертикальная прокрутка символов и остановка на итоге спина.
//
// Положение барабана — это две величины: head, индекс символа полосы в верхнем ряду окна,
// и offset, сдвиг вниз в долях окна от 0 до 1. Символы едут вниз, поэтому head убывает:
// символ, стоявший над окном, становится верхним.
//
// Движение отделено от показа: ReelMotion считает положение и ничего не знает про кадры,
// Reel раскладывает по этому положению символы. Так ход барабана проверяется тестом,
// а не только глазами.
//
// Барабан приходит к своему итогу двумя путями. Обычный — прокрутка: разгон, ровный ход
// и замедление до нужного символа. Второй — сдвиг: барабан с wild блокируется и съезжает
// на один символ вниз, пока остальные крутятся заново, см. Nudge Respin в GDD, раздел 4.3.
//
// Всё, что здесь считается в пикселях, живёт в координатах картинки барабанов, как и сетка
// окон в каталоге ассетов.

import { Container, Graphics, Sprite } from 'pixi.js';

import type { GameConfig, SymbolId } from '../api/gameConfig';
import type { CellMark, Field, FieldRow } from '../api/spin';
import { REELS_GRID } from '../assets/catalog';
import type { SymbolLibrary } from '../game/symbols';
import { sfx } from '../sound/sound';

/** Скорость ровного хода в окнах за секунду. */
const LOOP_SPEED: number = 18;

/** За сколько секунд барабан набирает скорость ровного хода. */
const SPEED_UP_SECONDS: number = 0.35;

/** Сколько окон занимает замедление перед остановкой. */
const SETTLE_WINDOWS: number = 3;

/**
 * Сколько длится замедление.
 *
 * Кривая замедления входит ровно со скоростью ровного хода, поэтому переход к ней не виден.
 * Производная 1-(1-t)^3 в нуле равна трём, отсюда и тройка в формуле.
 */
const SETTLE_SECONDS: number = (3 * SETTLE_WINDOWS) / LOOP_SPEED;

/** Пауза между остановками соседних барабанов: барабаны встают слева направо. */
const STOP_STEP_SECONDS: number = 0.18;

/**
 * Пауза между падениями соседних барабанов.
 *
 * Барабаны падают той же очередью, что и встают, только вдвое быстрее. Разом они падать
 * не должны: очередь слева направо читается как ускоренная остановка, а не как сбой.
 */
const SLAM_STEP_SECONDS: number = 0.08;

/** Скорость, ниже которой символы показываются чёткими, а не размытыми. */
const SHARP_SPEED: number = 3;

/** Сколько окон проходит сдвиг: барабан съезжает ровно на один символ. */
const NUDGE_WINDOWS: number = 1;

/**
 * Сколько длится сдвиг.
 *
 * Сдвиг — это не прокрутка, а показ: игрок обязан разглядеть, куда уехал wild и что встало
 * на его место. Поэтому символы на сдвиге остаются чёткими, как бы быстро он ни шёл.
 */
const NUDGE_SECONDS: number = 0.5;

/** Погрешность в долях окна, ниже которой путь считается пройденным. */
const EPSILON: number = 1e-9;

/**
 * Ряды доски по вертикали: где стоит середина и какой шаг между рядами.
 *
 * У поля 3x3 и у мини-слота 1x3 колонки общие, а ряды свои. Поэтому геометрия рядов идёт
 * отдельной величиной, а колонки считаются одной формулой на обе доски.
 */
export interface RowGrid {
  readonly centerY: number;
  readonly stepY: number;
}

/** Ряды основного поля 3x3. */
export const MAIN_ROWS: RowGrid = { centerY: REELS_GRID.main.centerY, stepY: REELS_GRID.main.stepY };

/** Ряд мини-слота 1x3 над полем. Ряд у него один, поэтому шаг равен высоте окна. */
export const BONUS_ROWS: RowGrid = { centerY: REELS_GRID.bonus.centerY, stepY: REELS_GRID.windowHeight };

/** Середина колонки барабана по горизонтали. */
export function columnX(column: number, reels: number): number {
  return REELS_GRID.centerX + (column - (reels - 1) / 2) * REELS_GRID.stepX;
}

/** Середина ряда доски по вертикали. Ряд задаётся дробным числом, когда барабан на ходу. */
export function rowY(row: number, rows: number, grid: RowGrid = MAIN_ROWS): number {
  return grid.centerY + (row - (rows - 1) / 2) * grid.stepY;
}

/** Ячейка поля: номер барабана и номер ряда. */
export interface Cell {
  readonly reel: number;
  readonly row: number;
}

/**
 * Где сейчас стоит помеченная ячейка.
 *
 * Барабаны знают своё положение на каждом кадре, а рисует надпись надстройка. Поэтому
 * барабаны отдают середину ячейки, а не рисуют число сами: их дело — символы, а не фичи.
 */
export interface MarkedCell {
  readonly reel: number;
  readonly value: number;

  /** Середина ячейки в координатах картинки барабанов. */
  readonly x: number;
  readonly y: number;

  /** Видна ли ячейка. Уехавшую за край поля и снятую под анимацию показывать нечего. */
  readonly visible: boolean;
}

/**
 * Через сколько секунд барабан ожидания останется крутиться один.
 *
 * Барабан перед ним заказан на паузу короче ровно на шаг очереди, а тормозит он столько же.
 * Первый в очереди крутится один с самого начала, поэтому ждать ему нечего.
 *
 * Отсчёт идёт от заказа остановки. Доводка до места, с которого начинается торможение,
 * занимает меньше одного окна ровного хода, и в счёт она не входит: это меньше кадра
 * анимации ожидания.
 *
 * @param queued Место барабана в очереди остановки. Заблокированные барабаны в неё не входят.
 */
export function aloneAfter(queued: number): number {
  if (queued <= 0)
    return 0;

  return (queued - 1) * STOP_STEP_SECONDS + SETTLE_SECONDS;
}

/** Приводит индекс к длине полосы: полоса замкнута в кольцо. */
function wrapIndex(index: number, length: number): number {
  return ((index % length) + length) % length;
}

/**
 * Сколько раз сменится верхний символ на оставшемся пути.
 *
 * Сдвиг внутри окна меньше единицы, поэтому целых смен ровно столько, сколько окон
 * в остатке пути, округлённых вверх.
 */
export function stepsLeft(distance: number): number {
  return Math.max(0, Math.ceil(distance - EPSILON));
}

/**
 * Фаза движения барабана.
 *
 * still — барабан стоит.
 * speedUp — разгон до скорости ровного хода.
 * loop — ровный ход, барабан ждёт итога спина.
 * approach — путь до места, с которого начнётся замедление.
 * settle — замедление до полной остановки на нужном символе.
 * nudge — сдвиг стоящего барабана на символ вниз, см. Nudge Respin в GDD, раздел 4.3.
 */
type ReelPhase = 'still' | 'speedUp' | 'loop' | 'approach' | 'settle' | 'nudge';

/** Окно барабанов: позиции на полосах и поле, которое они показывают. */
export interface ReelWindow {
  readonly stops: readonly number[];
  readonly field: Field;
}

/** Остановка барабанов: окно, метки и барабаны, пришедшие сдвигом. */
export interface ReelStop extends ReelWindow {
  /** Барабаны, которые пришли сюда сдвигом на символ вниз, а не прокруткой. */
  readonly nudged: readonly number[];

  /** Числа, которые надстройка повесила на ячейки поля. Пустой список — надстройки нет. */
  readonly marks: readonly CellMark[];
}

/**
 * Затяжка остановки одного барабана: он крутится дольше своей очереди.
 *
 * Так игра ждёт скаттер на последнем барабане, см. Anticipation в GDD, раздел 3.5. Про саму
 * анимацию ожидания барабаны не знают: их дело — держать барабан на ходу и сказать, когда
 * он остался крутиться один.
 */
export interface ReelHold {
  /** Барабан, который тянет остановку. */
  readonly reel: number;

  /** Насколько дольше он крутится. */
  readonly seconds: number;

  /** Зовётся, когда встали все барабаны перед ним. С этого мига он крутится один. */
  readonly onAlone: () => void;
}

/** Столбец поля сверху вниз: барабану от поля нужен только он. */
function fieldColumn(field: Field, column: number): FieldRow {
  return field.map((row: FieldRow) => row[column]);
}

/** Метки одного барабана: остальные лежат на соседях и его не касаются. */
function reelMarks(marks: readonly CellMark[], column: number): CellMark[] {
  return marks.filter((mark: CellMark) => mark.reel === column);
}

/** Ход одного барабана: положение на полосе, скорость и остановка. */
export class ReelMotion {
  readonly #length: number;

  #phase: ReelPhase = 'still';
  #head = 0;
  #offset = 0;
  #speed = 0;

  /**
   * Сколько раз сменится верхний символ до остановки.
   *
   * Пока остановка не начата, шагов бесконечно много: барабан крутится, и ряды поля
   * до окна ещё не доехали.
   */
  #steps: number = Number.POSITIVE_INFINITY;

  #target = 0;
  #wait = 0;
  #ordered = false;

  /** Сколько осталось до падения. Ноль — падение не заказано. */
  #dropIn = 0;

  #left = 0;
  #settled = 0;
  #settleSeconds = 0;

  constructor(length: number) {
    this.#length = length;
  }

  /** Индекс символа полосы в верхнем ряду окна. */
  get head(): number {
    return this.#head;
  }

  /** Сдвиг вниз в долях окна, от 0 до 1. */
  get offset(): number {
    return this.#offset;
  }

  /** Скорость в окнах за секунду. */
  get speed(): number {
    return this.#speed;
  }

  /**
   * Размыты ли символы барабана.
   *
   * Размытие показывает скорость, поэтому на прокрутке оно идёт по ней. Сдвиг из этого правила
   * выпадает: он короткий и оттого быстрый, но смотрят на нём как раз на символы.
   */
  get blurred(): boolean {
    return this.#speed > SHARP_SPEED && this.#phase !== 'nudge';
  }

  /** Сколько раз сменится верхний символ до остановки. */
  get steps(): number {
    return this.#steps;
  }

  /** Стоит ли барабан. */
  get stopped(): boolean {
    return this.#phase === 'still';
  }

  /** Пускает барабан: разгон, затем ровный ход. */
  start(): void {
    this.#phase = 'speedUp';
    this.#ordered = false;
    this.#dropIn = 0;
    this.#steps = Number.POSITIVE_INFINITY;
  }

  /** Заказывает остановку. Барабан начнёт тормозить, когда пройдёт заданное ожидание. */
  orderStop(target: number, wait: number): void {
    this.#target = target;
    this.#wait = wait;
    this.#ordered = true;
  }

  /**
   * Ставит барабан на позицию полосы без хода.
   *
   * Так игра показывает готовое положение: мини-слот на загрузке стоит там, где его оставили
   * прошлые раунды, и доезжать до этого места ему нечего.
   */
  place(target: number): void {
    this.#target = target;
    this.#ordered = false;
    this.#dropIn = 0;
    this.#speed = 0;
    this.#phase = 'still';
    this.#place(0);
  }

  /**
   * Сдвигает барабан на символ вниз, к заданной позиции полосы.
   *
   * Сдвиг — это то же замедление, только длиной в одно окно и без разгона перед ним: барабан
   * стоял, съехал на символ и встал снова. Барабан на ходу сдвигать нечего, он и так едет,
   * поэтому сдвиг начинается с любой фазы как с места.
   */
  nudge(target: number): void {
    this.#target = target;
    this.#ordered = false;
    this.#dropIn = 0;
    this.#settled = 0;
    this.#settleSeconds = 0;
    this.#phase = 'nudge';
    this.#place(NUDGE_WINDOWS);
  }

  /**
   * Ставит барабан на заказанную позицию, выждав свою паузу.
   *
   * Барабан без заказа продолжает крутиться: ронять его пока некуда. Повторное падение
   * назначенную очередь не двигает, иначе частые нажатия отодвигали бы её без конца.
   *
   * @param wait Пауза до падения в секундах. Ноль роняет барабан немедленно.
   */
  drop(wait: number): void {
    if (!this.#ordered && this.#phase !== 'approach' && this.#phase !== 'settle' && this.#phase !== 'nudge')
      return;

    if (wait > 0) {
      if (this.#dropIn === 0)
        this.#dropIn = wait;

      return;
    }

    this.#dropIn = 0;
    this.#ordered = false;
    this.#speed = 0;
    this.#phase = 'still';
    this.#place(0);
  }

  update(seconds: number): void {
    if (this.#dropIn > 0) {
      this.#dropIn -= seconds;

      if (this.#dropIn <= 0) {
        this.drop(0);
        return;
      }
    }

    if (this.#phase === 'still')
      return;

    if (this.#phase === 'speedUp')
      this.#speedUp(seconds);
    else if (this.#phase === 'loop')
      this.#loop(seconds);
    else if (this.#phase === 'approach')
      this.#approach(seconds);
    else
      this.#settle(seconds);
  }

  /** Сколько окон и за сколько секунд проходит нынешнее замедление. */
  get #slowing(): { windows: number; seconds: number } {
    if (this.#phase === 'nudge')
      return { windows: NUDGE_WINDOWS, seconds: NUDGE_SECONDS };

    return { windows: SETTLE_WINDOWS, seconds: SETTLE_SECONDS };
  }

  #speedUp(seconds: number): void {
    this.#speed = Math.min(LOOP_SPEED, this.#speed + (LOOP_SPEED / SPEED_UP_SECONDS) * seconds);
    this.#advance(this.#speed * seconds);

    if (this.#speed >= LOOP_SPEED)
      this.#phase = 'loop';
  }

  #loop(seconds: number): void {
    this.#speed = LOOP_SPEED;
    this.#advance(LOOP_SPEED * seconds);

    if (!this.#ordered)
      return;

    this.#wait -= seconds;
    if (this.#wait <= 0)
      this.#beginStop();
  }

  /**
   * Ставит барабан на путь до остановки.
   *
   * Путь всегда один и тот же: место на замедление и остаток начатого окна. Барабан для
   * этого перескакивает по полосе на нужный символ. Скачок не виден: символы размыты и на
   * ровном ходу меняются восемнадцать раз в секунду, а положение внутри окна остаётся своим.
   *
   * Дожидаться нужного символа на самой полосе нельзя: остановка тогда попадает только
   * в моменты, кратные обороту полосы, и барабаны не встают друг за другом.
   */
  #beginStop(): void {
    this.#ordered = false;
    this.#settled = 0;
    this.#settleSeconds = 0;
    this.#place(SETTLE_WINDOWS + 1 - this.#offset);
    this.#phase = 'approach';
  }

  #approach(seconds: number): void {
    this.#speed = LOOP_SPEED;

    const step: number = Math.min(LOOP_SPEED * seconds, this.#left - SETTLE_WINDOWS);
    this.#place(this.#left - step);

    if (this.#left > SETTLE_WINDOWS)
      return;

    // Остаток кадра уходит в замедление: иначе на стыке фаз барабан замирал бы на кадр.
    this.#phase = 'settle';
    this.#settle(seconds - step / LOOP_SPEED);
  }

  #settle(seconds: number): void {
    const slowing: { windows: number; seconds: number } = this.#slowing;

    this.#settleSeconds += seconds;

    const passed: number = Math.min(this.#settleSeconds / slowing.seconds, 1);
    const done: number = slowing.windows * (1 - (1 - passed) ** 3);

    // За кадр нулевой длины барабан не прошёл ничего, и скорость по нему не считается.
    // Обнулять её нельзя: символы стали бы чёткими на кадр посреди хода.
    if (seconds > 0)
      this.#speed = (done - this.#settled) / seconds;

    this.#settled = done;
    this.#place(slowing.windows - done);

    if (passed < 1)
      return;

    this.#speed = 0;
    this.#phase = 'still';
  }

  /**
   * Ставит барабан на заданный остаток пути до остановки.
   *
   * Положение считается от остатка, а не набирается сложением: за спин барабан проходит
   * тысячи кадров, и накопленная погрешность сдвинула бы его на целый символ мимо цели.
   */
  #place(left: number): void {
    this.#left = Math.max(0, left);
    this.#steps = stepsLeft(this.#left);
    this.#offset = this.#steps - this.#left;
    this.#head = wrapIndex(this.#target + this.#steps, this.#length);
  }

  #advance(distance: number): void {
    this.#offset += distance;

    while (this.#offset >= 1) {
      this.#offset -= 1;
      this.#head = wrapIndex(this.#head - 1, this.#length);
    }
  }
}

/** Один барабан: полоса символов и окно на несколько рядов. */
class Reel {
  readonly view: Container = new Container();

  readonly #motion: ReelMotion;
  readonly #strip: readonly SymbolId[];
  readonly #symbols: SymbolLibrary;
  readonly #sprites: Sprite[] = [];
  readonly #rows: number;
  readonly #grid: RowGrid;

  /** Ряды, символы которых сняты: их место занял показ выигрыша. */
  readonly #hidden = new Set<number>();

  /** Числа, повешенные надстройкой на ряды поля этого барабана. Ключ — ряд итога. */
  readonly #marks = new Map<number, number>();

  /** Столбец поля сверху вниз. Задан от заказа остановки до конца следующего спина. */
  #result: FieldRow | null = null;

  constructor(strip: readonly SymbolId[], symbols: SymbolLibrary, rows: number, grid: RowGrid, x: number) {
    this.#motion = new ReelMotion(strip.length);
    this.#strip = strip;
    this.#symbols = symbols;
    this.#rows = rows;
    this.#grid = grid;

    this.view.x = x;

    // Символов на один больше, чем рядов в окне: верхний въезжает в окно сверху.
    for (let slot = -1; slot < rows; slot++) {
      const sprite = new Sprite();
      sprite.anchor.set(0.5);

      this.#sprites.push(sprite);
      this.view.addChild(sprite);
    }

    this.#draw();
  }

  /** Стоит ли барабан. */
  get stopped(): boolean {
    return this.#motion.stopped;
  }

  /** Пускает барабан: разгон, затем ровный ход. */
  start(): void {
    this.#result = null;
    this.#hidden.clear();
    this.#marks.clear();
    this.#motion.start();
  }

  /**
   * Где сейчас стоят помеченные ячейки барабана.
   *
   * Ряд итога доезжает до окна вместе с барабаном: пока он не доехал, ячейка лежит выше поля
   * и не видна. Размытый барабан своих чисел тоже не показывает: на ходу их не прочесть.
   */
  markedCells(reel: number): MarkedCell[] {
    const cells: MarkedCell[] = [];

    for (const [row, value] of this.#marks) {
      const slot: number = row - this.#motion.steps;
      const inside: boolean = slot >= 0 && slot < this.#rows;

      cells.push({
        reel,
        value,
        x: this.view.x,
        y: rowY(slot + this.#motion.offset, this.#rows, this.#grid),
        visible: inside && !this.#motion.blurred && !this.#hidden.has(slot),
      });
    }

    return cells;
  }

  /** Оставляет снятыми ровно заданные ряды: на их месте показ выигрыша ставит свои анимации. */
  hideSymbols(rows: readonly number[]): void {
    this.#hidden.clear();

    for (const row of rows)
      this.#hidden.add(row);

    this.#draw();
  }

  /** Возвращает снятые символы на место. */
  showSymbols(): void {
    this.hideSymbols([]);
  }

  /** Заказывает остановку на символе полосы с показом заданного столбца поля. */
  orderStop(target: number, column: FieldRow, marks: readonly CellMark[], wait: number): void {
    this.#result = column;
    this.#setMarks(marks);
    this.#motion.orderStop(target, wait);
  }

  /** Ставит барабан на позицию полосы без хода, к показу заданного столбца поля. */
  place(target: number, column: FieldRow): void {
    this.#result = column;
    this.#hidden.clear();
    this.#marks.clear();
    this.#motion.place(target);
    this.#draw();
  }

  /** Сдвигает барабан на символ вниз, к показу заданного столбца поля. */
  nudge(target: number, column: FieldRow, marks: readonly CellMark[]): void {
    this.#result = column;
    this.#hidden.clear();
    this.#setMarks(marks);
    this.#motion.nudge(target);
    this.#draw();
  }

  /** Ставит барабан на заказанную позицию, выждав свою паузу. */
  drop(wait: number): void {
    this.#motion.drop(wait);
    this.#draw();
  }

  update(seconds: number): void {
    this.#motion.update(seconds);
    this.#draw();
  }

  /**
   * Символ в окне барабана.
   *
   * Символ из окна slot доедет до ряда slot + steps. Ряды поля берутся из итога спина,
   * а не из полосы: дальше фичи будут подменять символы прямо на поле.
   */
  #symbol(slot: number): SymbolId {
    if (this.#result !== null) {
      const row: number = slot + this.#motion.steps;

      if (row >= 0 && row < this.#result.length)
        return this.#result[row];
    }

    return this.#strip[wrapIndex(this.#motion.head + slot, this.#strip.length)];
  }

  /** Оставляет за барабаном ровно те метки, что легли на его ряды. */
  #setMarks(marks: readonly CellMark[]): void {
    this.#marks.clear();

    for (const mark of marks)
      this.#marks.set(mark.row, mark.value);
  }

  /** Раскладывает символы по окнам и берёт их кадры по ходу барабана. */
  #draw(): void {
    const blurred: boolean = this.#motion.blurred;

    this.#sprites.forEach((sprite: Sprite, index: number) => {
      const slot: number = index - 1;
      const id: SymbolId = this.#symbol(slot);

      sprite.texture = blurred ? this.#symbols.blurred(id) : this.#symbols.still(id);
      sprite.y = rowY(slot + this.#motion.offset, this.#rows, this.#grid);
      sprite.visible = !this.#hidden.has(slot);
    });
  }
}

/** Барабаны игрового поля. */
export class ReelsView {
  readonly view: Container = new Container();

  readonly #reels: Reel[];
  #onStopped: (() => void) | null = null;

  /** Сколько барабану ожидания осталось до того, как он останется крутиться один. Null — затяжки нет. */
  #alone: { left: number; onAlone: () => void } | null = null;

  /**
   * Сколько после стука глушится следующий.
   *
   * Вся очередь остановок укладывается в это время, поэтому обычный спин стучит один раз,
   * см. ADR-48. Барабан ожидания встаёт много позже неё и стучит своим стуком.
   */
  readonly #quietFor: number;

  /** Сколько барабанов стоит. По росту этого числа игра стучит. */
  #standing: number;

  /** Сколько ещё глушить стук. Ноль — следующая остановка слышна. */
  #quiet: number = 0;

  /**
   * @param grid Ряды доски. По умолчанию это ряды основного поля 3x3.
   */
  constructor(config: GameConfig, symbols: SymbolLibrary, grid: RowGrid = MAIN_ROWS) {
    const cells = new Container();

    this.#reels = config.reels.map((strip: readonly SymbolId[], column: number) => {
      const reel = new Reel(strip, symbols, config.rows, grid, columnX(column, config.reels.length));
      cells.addChild(reel.view);

      return reel;
    });

    // Барабан держит на один символ больше, чем видно в окне. Маска режет его по краю поля,
    // иначе символ над верхним рядом лёг бы поверх рамки машины.
    //
    // Край поля — это край крайних окон, промежутки между ними внутрь поля входят. Углы
    // скруглены по рамке машины, радиус снят с её картинки, см. REELS_GRID.
    //
    // Маска нарисована фигурой, а не взята картинкой. Картинку Pixi кладёт маской через
    // фильтр: он рисует поле в отдельную текстуру размером с его габарит на экране и режет
    // её по видимой области канваса. Ресайз окна этот размер меняет, и поле теряло нижний ряд.
    // Фигуру Pixi кладёт трафаретом, поэтому она не зависит ни от разрешения, ни от ресайза.
    const width: number = (config.reels.length - 1) * REELS_GRID.stepX + REELS_GRID.windowWidth;
    const height: number = (config.rows - 1) * grid.stepY + REELS_GRID.windowHeight;

    // Доска в один ряд ниже радиуса рамки, поэтому у неё углы скругляются по своей высоте.
    const radius: number = Math.min(REELS_GRID.cornerRadius, width / 2, height / 2);

    const windows = new Graphics().roundRect(REELS_GRID.centerX - width / 2, grid.centerY - height / 2, width, height, radius).fill(0xffffff);

    cells.mask = windows;
    this.view.addChild(cells, windows);

    // Барабаны встают очередью в STOP_STEP_SECONDS, поэтому вся она длится ровно столько.
    this.#quietFor = STOP_STEP_SECONDS * this.#reels.length;
    this.#standing = this.#reels.length;
  }

  /** Встали ли все барабаны. */
  get stopped(): boolean {
    return this.#reels.every((reel: Reel) => reel.stopped);
  }

  /**
   * Пускает барабаны: разгон, затем ровный ход.
   *
   * @param held Барабаны, которые остаются стоять: их держит wild, и придут они сдвигом,
   * а не прокруткой, см. nudge.
   */
  start(held: readonly number[] = []): void {
    this.#reels.forEach((reel: Reel, column: number) => {
      if (!held.includes(column))
        reel.start();
    });
  }

  /**
   * Сдвигает заблокированные барабаны спина на символ вниз.
   *
   * Сдвиг идёт разом с пуском остальных барабанов: барабан с wild блокируется и съезжает,
   * а свободные крутятся заново, см. Nudge Respin в GDD, раздел 4.3.
   */
  nudge(stop: ReelStop): void {
    for (const column of stop.nudged)
      this.#reels[column].nudge(stop.stops[column], fieldColumn(stop.field, column), reelMarks(stop.marks, column));
  }

  /** Где сейчас стоят помеченные ячейки поля. Надпись поверх них рисует надстройка. */
  markedCells(): MarkedCell[] {
    return this.#reels.flatMap((reel: Reel, column: number) => reel.markedCells(column));
  }

  /**
   * Ставит барабаны в заданное окно без хода.
   *
   * Так игра показывает готовое положение: мини-слот на загрузке стоит там, где его оставили
   * прошлые раунды, и доезжать до этого места ему нечего.
   */
  place(window: ReelWindow): void {
    this.#reels.forEach((reel: Reel, column: number) => {
      reel.place(window.stops[column], fieldColumn(window.field, column));
    });
  }

  /**
   * Снимает символы заданных ячеек.
   *
   * Показ выигрыша ставит на их место анимации, а две картинки одного символа в одной ячейке
   * разъезжаются, как только анимация двинет символ с места.
   */
  hideSymbols(cells: readonly Cell[]): void {
    this.#reels.forEach((reel: Reel, column: number) => {
      const rows: number[] = cells.filter((cell: Cell) => cell.reel === column).map((cell: Cell) => cell.row);
      reel.hideSymbols(rows);
    });
  }

  /** Возвращает снятые символы на место. */
  showSymbols(): void {
    for (const reel of this.#reels)
      reel.showSymbols();
  }

  /**
   * Останавливает барабаны на позициях из итога спина и ждёт остановки последнего.
   *
   * Барабаны встают слева направо: каждый следующий ждёт лишнюю паузу. Тормозят они все
   * одинаково долго, поэтому пауза между заказами и есть пауза между остановками.
   *
   * Заблокированный барабан останавливать нечего: он уже стоит на своей позиции со сдвига.
   * Очередь идёт по крутящимся барабанам, поэтому первый из них встаёт сразу, а не ждёт
   * очереди тех, кто и так стоит.
   *
   * @param hold Затяжка остановки одного барабана. Null — барабаны встают своей очередью.
   */
  stop(stop: ReelStop, hold: ReelHold | null = null): Promise<void> {
    const held = new Set<number>(stop.nudged);
    let queued = 0;

    this.#alone = null;

    this.#reels.forEach((reel: Reel, column: number) => {
      if (held.has(column))
        return;

      const waited: number = queued * STOP_STEP_SECONDS;
      const extra: number = hold !== null && hold.reel === column ? hold.seconds : 0;

      reel.orderStop(stop.stops[column], fieldColumn(stop.field, column), reelMarks(stop.marks, column), waited + extra);

      if (hold !== null && hold.reel === column)
        this.#alone = { left: aloneAfter(queued), onAlone: hold.onAlone };

      queued += 1;
    });

    return new Promise<void>((resolve) => {
      this.#onStopped = resolve;
    });
  }

  /**
   * Роняет барабаны на заказанные позиции.
   *
   * Барабаны падают очередью слева направо, как и встают, только вдвое быстрее. Барабан,
   * до которого очередь вставать ещё не дошла, падает вместе с остальными: позиция ему
   * уже заказана, ждал он только своей паузы.
   *
   * Очередь идёт по тем, кто ещё едет: вставший барабан ронять нечего, и держать за него
   * очередь тоже незачем.
   */
  drop(): void {
    let queued = 0;

    // Барабаны падают все разом, и ждать на них уже нечего: игрок торопит показ.
    this.#alone = null;

    for (const reel of this.#reels) {
      if (reel.stopped)
        continue;

      reel.drop(queued * SLAM_STEP_SECONDS);
      queued += 1;
    }

    this.#knock();
    this.#reportStopped();
  }

  update(seconds: number): void {
    this.#quiet = Math.max(this.#quiet - seconds, 0);

    for (const reel of this.#reels)
      reel.update(seconds);

    this.#knock();
    this.#advanceHold(seconds);
    this.#reportStopped();
  }

  /**
   * Стучит, если этим ходом встал очередной барабан.
   *
   * Стук один на всю очередь остановок: три щелчка подряд ухо слышит одним смазанным,
   * см. ADR-48. Поэтому стучит первый вставший барабан, а те, кто встал следом за ним,
   * идут молча, пока не кончится тишина.
   *
   * Считаются стоящие барабаны, а не события остановки: барабан встаёт двумя путями,
   * прокруткой и падением по скипу, а стоит он одинаково.
   */
  #knock(): void {
    const standing: number = this.#reels.filter((reel: Reel) => reel.stopped).length;
    const stopped: boolean = standing > this.#standing;

    this.#standing = standing;

    if (!stopped || this.#quiet > 0)
      return;

    this.#quiet = this.#quietFor;
    sfx('reelStop');
  }

  /** Ведёт затяжку и сообщает, что барабан ожидания остался крутиться один. */
  #advanceHold(seconds: number): void {
    const alone: { left: number; onAlone: () => void } | null = this.#alone;
    if (alone === null)
      return;

    alone.left -= seconds;
    if (alone.left > 0)
      return;

    this.#alone = null;
    alone.onAlone();
  }

  /** Сообщает раунду, что барабаны встали все до одного. */
  #reportStopped(): void {
    if (this.#onStopped === null)
      return;

    if (!this.stopped)
      return;

    const stopped: () => void = this.#onStopped;
    this.#onStopped = null;
    stopped();
  }
}
